import { DatePipe } from '@angular/common';
import {
  ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription, interval } from 'rxjs';

import {
  InquiryMessage, InquiryService, InquirySummary, InquiryThread, WhatsAppConsent,
} from '../../../services/inquiry.service';

type Role = 'all' | 'selling' | 'buying';

/**
 * "פניות" — every in-site conversation about a listing, as a seller and as a
 * buyer. Messages that arrive from WhatsApp (the other side answered there)
 * show up here too; the open thread refreshes itself every 15 seconds.
 */
@Component({
  selector: 'app-inquiries',
  standalone: true,
  imports: [RouterLink, DatePipe],
  templateUrl: './inquiries.component.html',
  styleUrls: ['./inquiries.component.scss'],
})
export class InquiriesComponent implements OnInit, OnDestroy {
  @ViewChild('scroller') scroller?: ElementRef<HTMLDivElement>;

  role: Role = 'all';
  items: InquirySummary[] = [];
  loading = true;
  failed = false;

  activeId: number | null = null;
  thread: InquiryThread | null = null;
  threadLoading = false;
  draft = '';
  sending = false;
  error = '';

  consent: WhatsAppConsent | null = null;
  consentPhone = '';
  consentSaving = false;
  consentMsg = '';
  consentErr = '';

  private subs: Subscription[] = [];

  constructor(
    private inquiries: InquiryService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subs.push(this.route.paramMap.subscribe(p => {
      const id = Number(p.get('id'));
      this.activeId = id > 0 ? id : null;
      if (this.activeId) this.loadThread(this.activeId);
      else this.thread = null;
    }));
    this.loadList();
    this.inquiries.consent().subscribe(c => {
      this.consent = c;
      this.consentPhone = c?.phone ? this.localPhone(c.phone) : '';
    });
    // WhatsApp replies land on the server — pick them up while the page is open.
    this.subs.push(interval(15000).subscribe(() => {
      if (document.hidden) return;
      this.loadList(true);
      if (this.activeId && !this.sending) this.loadThread(this.activeId, true);
    }));
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  setRole(role: Role): void {
    if (this.role === role) return;
    this.role = role;
    this.loadList();
  }

  loadList(silent = false): void {
    if (!silent) { this.loading = true; this.failed = false; }
    this.inquiries.list(this.role).subscribe({
      next: r => { this.items = r.results; this.loading = false; this.cdr.markForCheck(); },
      error: () => { this.loading = false; if (!silent) this.failed = true; },
    });
  }

  open(item: InquirySummary): void {
    this.router.navigate(['/personal/inquiries', item.id]);
  }

  back(): void {
    this.router.navigate(['/personal/inquiries']);
  }

  loadThread(id: number, silent = false): void {
    if (!silent) { this.threadLoading = true; this.error = ''; }
    this.inquiries.thread(id).subscribe({
      next: t => {
        const grew = !this.thread || this.thread.id !== t.id || this.thread.messages.length !== t.messages.length;
        this.thread = t;
        this.threadLoading = false;
        const row = this.items.find(i => i.id === id);
        if (row) row.unread = 0;
        this.inquiries.refreshUnread();
        if (grew) this.scrollDown();
      },
      error: () => {
        this.threadLoading = false;
        if (!silent) { this.thread = null; this.error = 'השיחה לא נמצאה'; }
      },
    });
  }

  onDraft(ev: Event): void {
    this.draft = (ev.target as HTMLTextAreaElement).value;
  }

  onKey(ev: KeyboardEvent): void {
    if (ev.key === 'Enter' && !ev.shiftKey && !(ev as any).isComposing) {
      ev.preventDefault();
      this.send();
    }
  }

  send(): void {
    const text = this.draft.trim();
    if (!text || !this.thread || this.sending) return;
    this.sending = true;
    this.error = '';
    this.inquiries.reply(this.thread.id, text).subscribe({
      next: m => {
        this.thread?.messages.push(m);
        this.draft = '';
        this.sending = false;
        this.scrollDown();
        this.loadList(true);
      },
      error: err => {
        this.sending = false;
        this.error = InquiryService.errorOf(err).detail;
      },
    });
  }

  // ── WhatsApp consent ──
  onConsentPhone(ev: Event): void {
    this.consentPhone = (ev.target as HTMLInputElement).value;
  }

  saveConsent(optIn: boolean): void {
    this.consentSaving = true;
    this.consentMsg = '';
    this.consentErr = '';
    this.inquiries.setConsent(optIn, optIn ? this.consentPhone.trim() : undefined).subscribe({
      next: c => {
        this.consent = c;
        this.consentPhone = c.phone ? this.localPhone(c.phone) : this.consentPhone;
        this.consentSaving = false;
        this.consentMsg = optIn ? 'מעולה! פניות ותשובות יגיעו גם לוואטסאפ שלך.' : 'קבלת הודעות בוואטסאפ בוטלה.';
      },
      error: err => {
        this.consentSaving = false;
        this.consentErr = InquiryService.errorOf(err).detail;
      },
    });
  }

  // ── view helpers ──
  initials(name: string): string {
    const p = (name || '').trim().split(/\s+/).filter(Boolean);
    return ((p[0]?.charAt(0) || '') + (p[1]?.charAt(0) || '')).replace('.', '') || '?';
  }

  ticks(m: InquiryMessage): { icon: string; cls: string; title: string } | null {
    switch (m.wa_status) {
      case 'queued': return { icon: 'mdi-timer-sand', cls: 'tick--wait', title: 'בדרך לוואטסאפ' };
      case 'sent': return { icon: 'mdi-check', cls: '', title: 'נשלח לוואטסאפ' };
      case 'delivered': return { icon: 'mdi-check-all', cls: '', title: 'נמסר בוואטסאפ' };
      case 'read': return { icon: 'mdi-check-all', cls: 'tick--read', title: 'נקרא בוואטסאפ' };
      case 'failed': return { icon: 'mdi-alert-circle', cls: 'tick--fail', title: 'לא נמסר בוואטסאפ — ההודעה זמינה באתר' };
      default: return null;
    }
  }

  trackById(_: number, x: { id: number }): number {
    return x.id;
  }

  private scrollDown(): void {
    setTimeout(() => {
      const el = this.scroller?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 30);
  }

  private localPhone(e164: string): string {
    return e164.startsWith('+972') ? '0' + e164.substring(4) : e164;
  }
}
